import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ErrorOutline,
  LockOutlined,
  Refresh,
  Home,
  Warning,
  ExpandMore,
  ExpandLess,
  InfoOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { detectThemeSync } from '../../utils/themeUtils';

const THEME_COLOR = '#4ce1b6';

/**
 * Standard Error State Component
 * Displays user-friendly error messages with appropriate actions
 */
const ErrorState = ({
  error = null,
  title = null,
  message = null,
  statusCode = null,
  onRetry = null,
  showHomeButton = false,
  showRefreshButton = true,
  icon = null,
}) => {
  const navigate = useNavigate();
  const isDark = detectThemeSync() === 'dark';
  const [showDetails, setShowDetails] = useState(false);

  // Determine error type and content
  const getErrorContent = () => {
    // If custom title/message provided, use those
    if (title || message) {
      return {
        title: title || 'Error',
        message: message || 'An error occurred',
        icon: icon || <ErrorOutline sx={{ fontSize: 64, color: '#f44336' }} />,
        severity: 'error',
      };
    }

    // Handle based on status code
    if (statusCode === 401 || (error?.statusCode === 401)) {
      return {
        title: 'Authentication Required',
        message: error?.userMessage || error?.message || 'You need to be logged in to access this page. Please log in and try again.',
        icon: <LockOutlined sx={{ fontSize: 80, color: '#ff9800' }} />,
        iconBg: 'rgba(255, 152, 0, 0.1)',
        severity: 'warning',
        actionLabel: 'Go to Login',
        action: () => {
          // Redirect to login or refresh page
          window.location.href = '/accounts/login/';
        },
      };
    }

    if (statusCode === 403 || (error?.statusCode === 403)) {
      return {
        title: 'Access Denied',
        message: error?.userMessage || error?.message || 'You do not have permission to access this resource. Please contact your administrator.',
        icon: <LockOutlined sx={{ fontSize: 80, color: '#f44336' }} />,
        iconBg: 'rgba(244, 67, 54, 0.1)',
        severity: 'error',
        actionLabel: 'Go to Dashboard',
        action: () => navigate('/dashboard/'),
      };
    }

    if (statusCode === 404 || (error?.statusCode === 404)) {
      return {
        title: 'Not Found',
        message: error?.userMessage || error?.message || 'The requested resource could not be found.',
        icon: <ErrorOutline sx={{ fontSize: 80, color: '#999' }} />,
        iconBg: 'rgba(158, 158, 158, 0.1)',
        severity: 'info',
        actionLabel: 'Go to Dashboard',
        action: () => navigate('/dashboard/'),
      };
    }

    if (statusCode === 500 || (error?.statusCode === 500)) {
      return {
        title: 'Server Error',
        message: error?.userMessage || error?.message || 'An internal server error occurred. Please try again later or contact support.',
        icon: <ErrorOutline sx={{ fontSize: 80, color: '#f44336' }} />,
        iconBg: 'rgba(244, 67, 54, 0.1)',
        severity: 'error',
      };
    }

    // Network errors
    if (error?.message?.includes('Network') || error?.message?.includes('network')) {
      return {
        title: 'Connection Error',
        message: error?.userMessage || error?.message || 'Unable to connect to the server. Please check your internet connection and try again.',
        icon: <Warning sx={{ fontSize: 80, color: '#ff9800' }} />,
        iconBg: 'rgba(255, 152, 0, 0.1)',
        severity: 'warning',
      };
    }

    // Default error
    return {
      title: 'Error',
      message: error?.userMessage || error?.message || 'An unexpected error occurred. Please try again.',
      icon: icon || <ErrorOutline sx={{ fontSize: 80, color: '#f44336' }} />,
      iconBg: 'rgba(244, 67, 54, 0.1)',
      severity: 'error',
    };
  };

  const errorContent = getErrorContent();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '500px',
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          maxWidth: 700,
          width: '100%',
          textAlign: 'center',
          backgroundColor: isDark ? '#2a2a31' : '#ffffff',
          border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
          borderRadius: 3,
          boxShadow: isDark 
            ? '0 8px 32px rgba(0, 0, 0, 0.3)' 
            : '0 8px 32px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          {/* Icon with background circle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 120,
              height: 120,
              borderRadius: '50%',
              backgroundColor: errorContent.iconBg || (isDark ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.08)'),
              mb: 1,
            }}
          >
            {errorContent.icon}
          </Box>

          {/* Status Code Chip */}
          {(statusCode || error?.statusCode) && (
            <Chip
              label={`Error ${statusCode || error?.statusCode}`}
              size="small"
              sx={{
                backgroundColor: errorContent.severity === 'error' ? '#f44336' : 
                                 errorContent.severity === 'warning' ? '#ff9800' : 
                                 errorContent.severity === 'info' ? '#2196f3' : '#999',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 28,
              }}
            />
          )}

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: isDark ? '#fff' : '#212121',
              mt: 1,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            {errorContent.title}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: isDark ? '#aaa' : '#666',
              mb: 2,
              maxWidth: '550px',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: 1.6,
            }}
          >
            {errorContent.message}
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'center',
              mt: 1,
            }}
          >
            {showRefreshButton && onRetry && (
              <Button
                variant="contained"
                size="large"
                startIcon={<Refresh />}
                onClick={onRetry}
                sx={{
                  backgroundColor: THEME_COLOR,
                  color: '#000',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  boxShadow: `0 4px 12px rgba(76, 225, 182, 0.3)`,
                  '&:hover': {
                    backgroundColor: THEME_COLOR,
                    opacity: 0.9,
                    boxShadow: `0 6px 16px rgba(76, 225, 182, 0.4)`,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Try Again
              </Button>
            )}

            {errorContent.action && (
              <Button
                variant="outlined"
                size="large"
                startIcon={errorContent.actionLabel === 'Go to Login' ? <LockOutlined /> : <Home />}
                onClick={errorContent.action}
                sx={{
                  borderColor: isDark ? '#444' : '#ddd',
                  borderWidth: 2,
                  color: isDark ? '#ddd' : '#666',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: THEME_COLOR,
                    borderWidth: 2,
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.08)',
                    color: THEME_COLOR,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {errorContent.actionLabel}
              </Button>
            )}

            {showHomeButton && !errorContent.action && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<Home />}
                onClick={() => navigate('/dashboard/')}
                sx={{
                  borderColor: isDark ? '#444' : '#ddd',
                  borderWidth: 2,
                  color: isDark ? '#ddd' : '#666',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: THEME_COLOR,
                    borderWidth: 2,
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.08)',
                    color: THEME_COLOR,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Go to Dashboard
              </Button>
            )}
          </Box>

          {/* Collapsible Error Details */}
          {error && (
            <Box sx={{ mt: 4, width: '100%' }}>
              <Button
                fullWidth
                variant="text"
                onClick={() => setShowDetails(!showDetails)}
                endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                startIcon={<InfoOutlined />}
                sx={{
                  color: isDark ? '#999' : '#666',
                  textTransform: 'none',
                  justifyContent: 'space-between',
                  mb: showDetails ? 2 : 0,
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {showDetails ? 'Hide' : 'Show'} Error Details
                </Typography>
              </Button>
              
              <Collapse in={showDetails}>
                <Box
                  sx={{
                    mt: 2,
                    p: 2.5,
                    backgroundColor: isDark ? '#1a1a1f' : '#f8f9fa',
                    border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                    borderRadius: 2,
                    borderLeft: `4px solid ${THEME_COLOR}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? '#888' : '#666',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      display: 'block',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Technical Details
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      color: isDark ? '#bbb' : '#444',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      lineHeight: 1.6,
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {error.statusCode && `Status Code: ${error.statusCode}\n`}
                    {error.statusText && `Status Text: ${error.statusText}\n`}
                    {error.message && `Message: ${error.message}\n`}
                    {error.userMessage && `User Message: ${error.userMessage}\n`}
                    {error.url && `URL: ${error.url}\n`}
                    {error.method && `Method: ${error.method}\n`}
                    {error.responseData && `\nResponse Data:\n${JSON.stringify(error.responseData, null, 2)}`}
                  </Box>
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ErrorState;

